import { Clear, Search } from "@mui/icons-material";
import {
	Autocomplete,
	Box,
	Button,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { addMonths, endOfMonth, format, parseISO, startOfMonth, subDays, subMonths, subYears } from "date-fns";
import { useEffect, useState } from "react";
import { useDebounce } from "../../../shared/hooks";
import { clientService } from "../../../shared/services/api/client/clientService";
import { BORDERO_STATUS_LABELS } from "../constants/borderoStatusOptions";

export type FinancialBorderoClientOption = {
	id: string;
	label: string;
};

// "Todos" usa o literal "Todos" (não string vazia) porque é o valor que este módulo já usa pra
// "sem filtro" — ver filterInitialParams.status em FinancialBorderoPresentation.tsx.
const STATUS_OPTIONS = [
	{ value: "Todos", label: "Todos" },
	...Object.entries(BORDERO_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

const SEARCH_FIELD_OPTIONS = [
	{ value: "documento", label: "Documento" },
	{ value: "nome", label: "Nome" },
];

const TYPE_PERIOD_OPTIONS = [
	{ value: "criacao", label: "Criação" },
	{ value: "vencimento", label: "Vencimento" },
	{ value: "provisionado", label: "Provisionamento" },
];

interface ToolbarFinancialBorderoProps {
	textSearch?: string;
	onChangeTextSearch?: (value: string) => void;
	searchField?: string;
	onChangeSearchField?: (value: string) => void;

	status?: string;
	onChangeStatus?: (value: string) => void;

	typePeriod?: string;
	onChangeTypePeriod?: (value: string) => void;
	onChangeDateStart?: (value: string) => void;
	onChangeDateEnd?: (value: string) => void;

	onChangeClient?: (clientId: string | null) => void;

	onClickSearch?: () => void;
}

// Toolbar do módulo Financeiro > Borderô, no mesmo padrão visual usado em Garantias/Análises/
// Repasses (Paper + Período com opções rápidas + Personalizado) — antes usava o componente
// `Toolbar` legado, que não seguia mais esse padrão (pedido da operação, GARV2-1463/1464).
export function ToolbarFinancialBordero({
	textSearch = "",
	onChangeTextSearch,
	searchField = "documento",
	onChangeSearchField,
	status = "Todos",
	onChangeStatus,
	typePeriod = "criacao",
	onChangeTypePeriod,
	onChangeDateStart,
	onChangeDateEnd,
	onChangeClient,
	onClickSearch,
}: ToolbarFinancialBorderoProps) {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const today = new Date();

	const [startDate, setStartDate] = useState(format(startOfMonth(today), "yyyy-MM-dd"));
	const [endDate, setEndDate] = useState(format(endOfMonth(today), "yyyy-MM-dd"));
	const [periodType, setPeriodType] = useState("1_mes");

	const { debounce } = useDebounce();
	const [clientInput, setClientInput] = useState("");
	const [clientSelected, setClientSelected] = useState<FinancialBorderoClientOption | null>(null);
	const [clientLoading, setClientLoading] = useState(false);
	const [clientOptions, setClientOptions] = useState<FinancialBorderoClientOption[]>([]);

	const getDateRangeFromPeriod = (period: string): [string, string] => {
		const now = new Date();
		let start: Date = now;
		let end: Date = now;

		switch (period) {
			case "15_dias":
				start = subDays(now, 15);
				end = now;
				break;
			case "1_mes":
				start = startOfMonth(now);
				end = endOfMonth(now);
				break;
			case "2_meses":
				start = startOfMonth(subMonths(now, 1));
				end = endOfMonth(now);
				break;
			case "3_meses":
				start = startOfMonth(subMonths(now, 2));
				end = endOfMonth(now);
				break;
			case "1_ano":
				start = startOfMonth(subYears(now, 1));
				end = endOfMonth(now);
				break;
			case "mes_anterior":
				start = startOfMonth(subMonths(now, 1));
				end = endOfMonth(subMonths(now, 1));
				break;
			case "proximo_mes":
				start = startOfMonth(addMonths(now, 1));
				end = endOfMonth(addMonths(now, 1));
				break;
		}

		return [format(start, "yyyy-MM-dd"), format(end, "yyyy-MM-dd")];
	};

	const handlePeriodChange = (e: SelectChangeEvent) => {
		const value = e.target.value;
		setPeriodType(value);

		if (value !== "personalizado") {
			const [newStart, newEnd] = getDateRangeFromPeriod(value);
			setStartDate(newStart);
			setEndDate(newEnd);
			onChangeDateStart?.(newStart);
			onChangeDateEnd?.(newEnd);
		}
	};

	useEffect(() => {
		if (!clientInput) {
			setClientOptions([]);
			return;
		}

		setClientLoading(true);

		debounce(() => {
			clientService
				.getSelect(1, clientInput.trim())
				.then((result: any) => {
					if (result instanceof Error) return;

					const options = result?.data?.map((c: any) => ({
						id: c.id,
						label: c.name,
					}));

					setClientOptions(options ?? []);
				})
				.finally(() => setClientLoading(false));
		});
		// eslint-disable-next-line
	}, [clientInput]);

	const handleSelectClient = (value: FinancialBorderoClientOption | null) => {
		setClientSelected(value);
		onChangeClient?.(value?.id ?? null);
	};

	return (
		<Box component={Paper} display="flex" flexDirection="column" padding={2} gap={1} mx={1}>
			<Grid container spacing={2} direction={isSmallScreen ? "column" : "row"} alignItems="center">
				<Grid item xs={12} sm={6} md={2}>
					<FormControl fullWidth size="small">
						<InputLabel id="bordero-type-period-label">Data de</InputLabel>
						<Select
							labelId="bordero-type-period-label"
							value={typePeriod}
							label="Data de"
							onChange={(e: SelectChangeEvent) => onChangeTypePeriod?.(e.target.value)}
						>
							{TYPE_PERIOD_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>

				<Grid item xs={12} sm={6} md={2}>
					<FormControl fullWidth size="small">
						<InputLabel id="bordero-period-label">Período</InputLabel>
						<Select labelId="bordero-period-label" value={periodType} label="Período" onChange={handlePeriodChange}>
							<MenuItem value="15_dias">Últimos 15 dias</MenuItem>
							<MenuItem value="mes_anterior">Mês Anterior</MenuItem>
							<MenuItem value="1_mes">Mês Atual</MenuItem>
							<MenuItem value="proximo_mes">Próximo Mês</MenuItem>
							<MenuItem value="2_meses">Últimos 2 meses</MenuItem>
							<MenuItem value="3_meses">Últimos 3 meses</MenuItem>
							<MenuItem value="1_ano">Último ano</MenuItem>
							<MenuItem value="personalizado">Personalizado</MenuItem>
						</Select>
					</FormControl>
				</Grid>

				{periodType === "personalizado" && (
					<>
						<Grid item xs={12} sm={6} md={2}>
							<TextField
								fullWidth
								size="small"
								type="date"
								label="Data Inicial"
								value={startDate}
								onChange={(e) => {
									setStartDate(e.target.value);
									onChangeDateStart?.(e.target.value);
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<TextField
								fullWidth
								size="small"
								type="date"
								label="Data Final"
								value={endDate}
								onChange={(e) => {
									setEndDate(e.target.value);
									onChangeDateEnd?.(e.target.value);
								}}
							/>
						</Grid>
					</>
				)}

				<Grid item xs={12} sm={6} md={2}>
					<FormControl fullWidth size="small">
						<InputLabel id="bordero-status-label">Status</InputLabel>
						<Select
							labelId="bordero-status-label"
							value={status}
							label="Status"
							onChange={(e: SelectChangeEvent) => onChangeStatus?.(e.target.value)}
						>
							{STATUS_OPTIONS.map((option) => (
								<MenuItem key={option.value || "todos"} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>

				<Grid item xs={12} sm={6} md={3}>
					<Autocomplete
						fullWidth
						size="small"
						options={clientOptions}
						value={clientSelected}
						onChange={(_, v) => handleSelectClient(v)}
						onInputChange={(_, value) => setClientInput(value)}
						renderInput={(params) => <TextField {...params} label="Imobiliária" />}
					/>
				</Grid>
			</Grid>

			<Grid container spacing={2} direction={isSmallScreen ? "column" : "row"} alignItems="center">
				<Grid item xs={12} sm={4} md={2}>
					<FormControl fullWidth size="small">
						<InputLabel id="bordero-search-field-label">Buscar por</InputLabel>
						<Select
							labelId="bordero-search-field-label"
							value={searchField}
							label="Buscar por"
							onChange={(e: SelectChangeEvent) => onChangeSearchField?.(e.target.value)}
						>
							{SEARCH_FIELD_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>

				<Grid item xs={12} sm={8} md={4}>
					<TextField
						fullWidth
						size="small"
						placeholder="Pesquisar..."
						value={textSearch}
						onChange={(e) => onChangeTextSearch?.(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search />
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position="end">
									<IconButton onClick={() => onChangeTextSearch?.("")}>
										<Clear />
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
				</Grid>

				<Grid item xs={12} sm={12} md>
					<Box display="flex" justifyContent="flex-start" gap={1}>
						<Button variant="contained" onClick={onClickSearch}>
							Pesquisar
						</Button>
					</Box>
				</Grid>
			</Grid>

			<Typography variant="body2" sx={{ mt: 0.5, ml: 1, fontStyle: "italic", color: "text.secondary" }}>
				Período: {format(parseISO(startDate), "dd/MM/yyyy")} - {format(parseISO(endDate), "dd/MM/yyyy")}
			</Typography>
		</Box>
	);
}
